import { ensureKeypair } from "./cryptoKeys";
import { looksLikeCompactJWE, decryptCompactJwe } from "./jweDecrypt";

async function tryDecryptInPlace(response) {
  // Don’t touch empty bodies or HEAD/204
  if (response.status === 204 || !response.data) return { ok: false };

  const ct = response.headers?.["content-type"] || "";
  const flagged = response.headers?.["x-encrypted"] === "1";
  const isJose = ct.includes("application/jose");
  const encrypted = flagged || isJose || looksLikeCompactJWE(response.data);

  if (!encrypted || typeof response.data !== "string") return { ok: false };

  response.data = await decryptCompactJwe(response.data);
  return { ok: true };
}

export function attachJweInterceptors(instance) {
  if (!instance || instance.__jweAttached) return;
  instance.__jweAttached = true;

  // SUCCESS path
  instance.interceptors.response.use(
    async (response) => {
      try {
        await tryDecryptInPlace(response);
        return response; // plain JSON or decrypted — either way, resolve
      } catch (err) {
        // IMPORTANT: do not throw — re-key + register + retry once
        if (response.config?.__jweRetry) return response; // already retried; give up silently

        const { publicJwk } = await ensureKeypair({ force: true });
        try {
          await instance.post("crypto/register-key", { publicJwk }, { withCredentials: true });
          const retryCfg = { ...response.config, __jweRetry: true };
          return await instance.request(retryCfg);
        } catch {
          // Still failing — return original response so caller can handle normally
          return response;
        }
      }
    },

    // ERROR path
    async (error) => {
      const res = error?.response;
      const cfg = error?.config;

      // If server has no key (428), register + retry once
      if (res?.status === 428 && cfg && !cfg.__jweRetry) {
        const { publicJwk } = await ensureKeypair();
        await instance.post("crypto/register-key", { publicJwk }, { withCredentials: true });
        return instance.request({ ...cfg, __jweRetry: true });
      }

      // If the error body itself is JWE, try to decrypt to a readable error
      if (res && typeof res.data === "string" && looksLikeCompactJWE(res.data)) {
        try {
          res.data = await decryptCompactJwe(res.data);
        } catch { /* nothing to do here */ }
      }

      return Promise.reject(error);
    }
  );
}
